import {
  format,
  isAfter,
  isToday,
  isYesterday,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import type { ExtendedGenerationRecord } from 'dynamo-daos';
import { Fragment } from 'react';
import { useSnapshot } from 'valtio';

import Link from '@cartwhl/common/components/Link';
import { FromStore, Project } from '@cartwhl/common/types';
import Image from '@cartwhl/ui/atoms/Image';
import LoadingBlock from '@cartwhl/ui/atoms/LoadingBlock';
import Text from '@cartwhl/ui/base/Text';

import { projectStore } from 'stores';
import { getJobIDsFromProjectTimeline } from 'stores/projectStore';

function displayLastUpdatedAt(updatedAtDate: Date): string {
  if (!(updatedAtDate instanceof Date) || isNaN(updatedAtDate.getTime())) {
    return 'now';
  }

  const now = new Date();
  const diffMs = Math.max(0, now.getTime() - updatedAtDate.getTime());
  const seconds = Math.floor(diffMs / 1000);

  if (seconds < 45) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 2) return '1m ago';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 2) return '1hr ago';
  if (hours < 24) return `${hours}hr ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  const years = Math.floor(days / 365);
  return `${years}yr ago`;
}

export default function PoseGenerationHistory({
  historyScope,
  searchTerm,
}: {
  historyScope: 'all' | 'renders';
  searchTerm: string;
}) {
  const { currentProject, currentProjectGenerationHistory } = useSnapshot(
    projectStore.state,
  );

  const generationHistory =
    searchTerm.length > 1
      ? currentProjectGenerationHistory.data?.items?.filter((generation) => {
          const lowerSearch = searchTerm.toLowerCase();
          let matches = false;

          if ('prompt' in generation && typeof generation.prompt === 'string') {
            matches =
              matches || generation.prompt.toLowerCase().includes(lowerSearch);
          }
          if ('title' in generation && typeof generation.title === 'string') {
            matches =
              matches || generation.title.toLowerCase().includes(lowerSearch);
          }
          if (
            'sessionName' in generation &&
            typeof generation.sessionName === 'string'
          ) {
            matches =
              matches ||
              generation.sessionName.toLowerCase().includes(lowerSearch);
          }
          if (
            'renderPrompt' in generation &&
            typeof generation.renderPrompt === 'string'
          ) {
            matches =
              matches ||
              generation.renderPrompt.toLowerCase().includes(lowerSearch);
          }

          return matches;
        })
      : currentProjectGenerationHistory.data?.items;

  if (
    currentProjectGenerationHistory.status === 'initial' ||
    generationHistory === undefined
  ) {
    return null;
  }

  const groupedGenerations =
    groupGenerationHistoryByCreationDate(generationHistory);

  const timelineJobIDs = currentProject.data
    ? getJobIDsFromProjectTimeline(currentProject.data as Project)
    : [];

  return (
    <>
      {generationHistory === undefined ? (
        <div className="grid grid-cols-1 gap-2">
          <LoadingBlock variant="accordion" />
          <LoadingBlock variant="accordion" />
          <LoadingBlock variant="accordion" />
          <LoadingBlock variant="accordion" />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {Object.entries(groupedGenerations).map(([key, generationGroup]) => (
            <Fragment key={key}>
              <Text
                variant="studio/body/sm"
                as="div"
                className="text-gray-60 dark:text-gray-45 flex h-[34px] items-center px-2"
              >
                {key}
              </Text>
              {generationGroup.map(
                (generation: FromStore<ExtendedGenerationRecord>) => {
                  // Mitch TODO: Clean this whole thing up, we should have better types now
                  // that Vaniya has done the backend updates.
                  const extra = generation as Partial<{
                    title: string;
                    renderPrompt: string;
                    lastUpdatedAt: Date | string;
                    renderedPngURL: string;
                    studioPreviewURL: string;
                  }>;

                  if (
                    historyScope === 'renders' &&
                    (!('renderedPngURL' in generation) ||
                      generation.renderedPngURL === '')
                  ) {
                    return null;
                  }

                  const title = extra?.title;
                  const renderPrompt = extra?.renderPrompt || title;
                  const primaryThumbnail =
                    extra?.renderedPngURL || extra?.studioPreviewURL;
                  const updatedAtRaw =
                    extra?.lastUpdatedAt || generation?.createdAt;
                  const updatedAtDate = updatedAtRaw
                    ? new Date(updatedAtRaw)
                    : new Date();
                  const headerThumbnail =
                    extra && extra.renderedPngURL
                      ? extra.studioPreviewURL
                      : undefined;

                  const borderColor = timelineJobIDs.includes(generation.jobID)
                    ? 'border-blue dark:border-blue'
                    : 'border-gray-30 dark:border-gray-60';
                  return (
                    <div
                      key={generation.jobID}
                      className={`${borderColor} dark:bg-gray-90 mb-2 cursor-pointer rounded-2xl border bg-white ${borderColor}`}
                      onClick={() => {
                        projectStore.updateProjectPrimaryPose(
                          currentProject.data?.projectID || '',
                          generation.jobID,
                        );
                      }}
                    >
                      <div className="flex items-center justify-between px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2">
                          {headerThumbnail ? (
                            <Image
                              src={headerThumbnail}
                              alt=""
                              className="h-6 w-6 rounded-sm object-cover"
                              crossOrigin="anonymous"
                            />
                          ) : null}
                          <Text
                            variant="studio/body/sm"
                            as="div"
                            className="truncate"
                          >
                            {title}
                          </Text>
                        </div>
                        <Text
                          variant="studio/body/sm"
                          as="div"
                          className="text-gray-60"
                          title={format(
                            updatedAtDate,
                            'MMM d, yyyy, h:mm:ss a',
                          )}
                        >
                          {displayLastUpdatedAt(updatedAtDate)}
                        </Text>
                      </div>

                      <div className="flex gap-4 p-3">
                        <div className="max-w-1/2 relative aspect-video flex-[0_1_50%] overflow-hidden rounded-md bg-black">
                          <Image
                            src={primaryThumbnail}
                            alt={renderPrompt}
                            className="h-full w-full object-contain"
                            crossOrigin="anonymous"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <Text
                            variant="studio/body/xs"
                            className="text-gray-60 dark:text-gray-45 line-clamp-4 whitespace-pre-line break-words"
                            title={renderPrompt}
                          >
                            {renderPrompt}
                          </Text>
                        </div>
                      </div>
                    </div>
                  );
                },
              )}
            </Fragment>
          ))}

          {currentProject.status === 'ready' &&
            generationHistory.length === 0 && (
              <Text
                variant="studio/body/sm"
                className="text-gray-60 text-center"
              >
                This project's history is empty. Previous poses from this
                project will show up here.
              </Text>
            )}
          <Text
            variant="studio/body/sm"
            className="text-gray-60 mt-8 text-center"
          >
            Looking for poses from a previous project? Go to{' '}
            <Link className="text-blue underline" href="/projects">
              your projects
            </Link>
          </Text>
        </div>
      )}
    </>
  );
}

const DateGroups = {
  ['Today']: isToday,
  ['Yesterday']: isYesterday,
  ['Previous 7 days']: (date: string) => isAfter(date, subDays(new Date(), 7)),
  ['Previous 30 days']: (date: string) =>
    isAfter(date, subMonths(new Date(), 1)),
  ['rest']: () => true,
};

function groupGenerationHistoryByCreationDate(
  generationHistory?: FromStore<ExtendedGenerationRecord[]>,
) {
  const output: Record<string, FromStore<ExtendedGenerationRecord>[]> = {};

  for (const generation of generationHistory || []) {
    for (const [key, testFunction] of Object.entries(DateGroups)) {
      if (key === 'rest') {
        if (isAfter(generation.createdAt, subYears(new Date(), 1))) {
          output[format(generation.createdAt, 'MMMM')] =
            output[format(generation.createdAt, 'MMMM')] || [];
          output[format(generation.createdAt, 'MMMM')].push(generation);
        } else {
          output[format(generation.createdAt, 'MMMM yyy')] =
            output[format(generation.createdAt, 'MMMM yyy')] || [];
          output[format(generation.createdAt, 'MMMM yyy')].push(generation);
        }
      } else if (testFunction(generation.createdAt)) {
        output[key] = output[key] || [];
        output[key].push(generation);
        break;
      }
    }
  }

  return output;
}
